import { useState, useCallback, useRef, useEffect } from 'react'

const useUploadProgress = (asyncFunction: Function) => {
	const [progress, setProgress] = useState<number>(0)
	const [isLoadingProgress, setIsLoadingProgress] = useState<boolean>(false)
	const intervalRef = useRef<NodeJS.Timeout | null>(null)
	const functionCompleted = useRef<boolean>(false)
	const maxTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		return () => {
			clearIntervalRef()
			clearMaxTimeout()
		}
	}, [])

	const clearIntervalRef = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}
	}

	const clearMaxTimeout = () => {
		if (maxTimeoutRef.current) {
			clearTimeout(maxTimeoutRef.current)
			maxTimeoutRef.current = null
		}
	}

	const finishProgress = async (success = true) => {
		clearIntervalRef()

		if (success) {
			setProgress(100)
		} else {
			setProgress(0)
		}

		await new Promise<void>((res) => setTimeout(res, 200))

		setIsLoadingProgress(false)
		clearMaxTimeout()
		functionCompleted.current = true
	}

	const executeWithProgress = useCallback(
		async (...args: any[]) => {
			setProgress(0)
			setIsLoadingProgress(true)
			functionCompleted.current = false

			clearMaxTimeout()
			maxTimeoutRef.current = setTimeout(() => {
				if (!functionCompleted.current) {
					console.warn(
						'La operación tomó demasiado tiempo y se completó automáticamente'
					)
					finishProgress(true)
				}
			}, 30000)

			clearIntervalRef()
			intervalRef.current = setInterval(() => {
				setProgress((prev) => {
					if (prev < 99) {
						const inc = prev < 30 ? 3 : prev < 60 ? 2 : prev < 90 ? 1 : 0.5
						return Math.min(prev + inc, 99)
					}
					return prev
				})
			}, 100)

			try {
				let result

				try {
					result = await asyncFunction(...args)
				} catch (innerError) {
					console.error('Error en la función asíncrona:', innerError)
					await finishProgress(false)
					throw innerError
				}

				await finishProgress(true)
				return result
			} catch (err) {
				console.error('Error en executeWithProgress:', err)
				await finishProgress(false)
				throw err
			}
		},
		[asyncFunction]
	)

	return {
		executeWithProgress,
		progress,
		isLoadingProgress,
		resetProgress: () => {
			clearIntervalRef()
			clearMaxTimeout()
			setProgress(0)
			setIsLoadingProgress(false)
			functionCompleted.current = true
		},
		forceComplete: async () => {
			await finishProgress(true)
		},
	}
}

export default useUploadProgress
